// Lib
import React from 'react';
import PropTypes from 'prop-types';

// Plugins
import createSingleLinePlugin from 'draft-js-single-line-plugin';
import Editor from '@draft-js-plugins/editor';
import { EditorState } from 'draft-js';
import createFilterTagPlugin from './plugins/filterTag';

// Utils
import platformSingleton from '../../../../platform/platformSingleton';
import { hasChanged } from '../../../../utils/react/propsComparisons';
import createEditorState from '../../../editor/customRichUtils/createEditorState';
import selectAll from '../../../editor/customRichUtils/selectAll';

// Constants
import { KEY_CODES } from '../../../../utils/keyboard/keyConstants';

// Prop comparisons
const searchQueryHasChanged = hasChanged('searchQuery');

// Plugins
const filterTagPlugin = createFilterTagPlugin();
const singleLinePlugin = createSingleLinePlugin();

const plugins = [filterTagPlugin, singleLinePlugin];

class SearchFormEditor extends React.Component {
    constructor(props) {
        super(props);
        this.state = { editorState: null };

        this.inputRef = React.createRef();
    }

    componentWillMount() {
        const { searchQuery } = this.props;

        this.setState({
            editorState: createEditorState(searchQuery),
        });
    }

    componentDidMount() {
        requestAnimationFrame(this.selectSearchInput);
    }

    componentDidUpdate(prevProps) {
        const { searchQuery } = this.props;

        if (searchQueryHasChanged(prevProps, this.props) && this.getQuery() !== searchQuery) {
            this.onChange(EditorState.moveFocusToEnd(createEditorState(searchQuery, this.state.editorState)));
        }
    }

    onChange = (editorState) => {
        const query = editorState.getCurrentContent().getPlainText();
        const { searchQuery, disabled } = this.props;

        if (disabled) return false;

        if (query !== searchQuery) this.props.onChange(query);

        this.setState({ editorState });
    };

    getQuery = () => this.state.editorState.getCurrentContent().getPlainText();

    selectSearchInput = () => {
        this.inputRef.current.focus();
        this.setState(({ editorState, ...rest }) => ({ editorState: selectAll(editorState), ...rest }));
    };

    blurSearchInput = () => {
        this.inputRef.current.blur();
    };

    handleReturn = (event) => {
        event.preventDefault();
        this.props.handleReturn(this.getQuery());

        if (platformSingleton.features.isTouch) {
            this.blurSearchInput();
        }

        return 'handled';
    };

    handleUpArrow = (event) => {
        event.preventDefault();
        this.props.moveListFocusUp && this.props.moveListFocusUp();
    };

    handleDownArrow = (event) => {
        event.preventDefault();
        this.props.moveListFocusDown && this.props.moveListFocusDown();
    };

    handleEscapeKey = (event) => {
        event.preventDefault();
        this.props.closeSearch();
    };

    handleTabKey = (event) => {
        event.preventDefault();

        event.shiftKey
            ? this.props.moveListFocusUp && this.props.moveListFocusUp()
            : this.props.moveListFocusDown && this.props.moveListFocusDown();
    };

    keyBindingFn = (event) => {
        switch (event.keyCode) {
            case KEY_CODES.DOWN_ARROW:
                return this.handleDownArrow(event);
            case KEY_CODES.UP_ARROW:
                return this.handleUpArrow(event);
            case KEY_CODES.ESC:
                return this.handleEscapeKey(event);
            case KEY_CODES.TAB:
                return this.handleTabKey(event);
            default:
                return undefined;
        }
    };

    focus = () => {
        this.inputRef.current.focus();
    };

    render() {
        const { editorState } = this.state;
        const { searchInputPlaceholder, disabled } = this.props;

        return (
            <Editor
                className="SearchInput SearchFormEditor"
                readOnly={disabled}
                placeholder={searchInputPlaceholder}
                ref={this.inputRef}
                editorState={editorState}
                blockRenderMap={singleLinePlugin.blockRenderMap}
                stripPastedStyles
                plugins={plugins}
                onChange={this.onChange}
                handleReturn={this.handleReturn}
                keyBindingFn={this.keyBindingFn}
            />
        );
    }
}

SearchFormEditor.propTypes = {
    onChange: PropTypes.func.isRequired,
    searchQuery: PropTypes.string.isRequired,
    searchInputPlaceholder: PropTypes.string,

    handleReturn: PropTypes.func.isRequired,
    moveListFocusUp: PropTypes.func,
    moveListFocusDown: PropTypes.func,
    closeSearch: PropTypes.func,
    disabled: PropTypes.bool,
};

export default SearchFormEditor;
